import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { projectsAPI, driversAPI, segmentsAPI, segmentTypesAPI, stakeholdersAPI } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from './AuthContext';

const ProjectContext = createContext(null);

const LAST_PROJECT_KEY = 'pulse_last_project_id';

export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [segmentTypes, setSegmentTypes] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isProjectAdmin, setIsProjectAdmin] = useState(false);
  const [isAccountOwner, setIsAccountOwner] = useState(false);
  const [userPermissions, setUserPermissions] = useState({ allowed_pages: null, allowed_segments: null, is_admin: true });

  const fetchProjects = useCallback(async () => {
    try {
      // Use the new API that returns projects with role info
      const response = await projectsAPI.listWithRoles();
      setProjects(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      // Fallback to regular list if new endpoint fails
      try {
        const fallbackResponse = await projectsAPI.list();
        setProjects(fallbackResponse.data);
        return fallbackResponse.data;
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
        return [];
      }
    }
  }, []);

  // Restore last project on initial load
  useEffect(() => {
    const restoreLastProject = async () => {
      if (!user) return;
      
      const lastProjectId = localStorage.getItem(LAST_PROJECT_KEY);
      if (lastProjectId) {
        try {
          const projectsList = await fetchProjects();
          const projectExists = projectsList.find(p => p.id === lastProjectId);
          if (projectExists) {
            await selectProject(lastProjectId);
          }
        } catch (err) {
          console.error('Failed to restore last project:', err);
        }
      }
    };
    
    restoreLastProject();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectProject = useCallback(async (projectId) => {
    if (!projectId) {
      setCurrentProject(null);
      setDrivers([]);
      setSegmentTypes([]);
      setSegments([]);
      setIsProjectAdmin(false);
      setIsAccountOwner(false);
      localStorage.removeItem(LAST_PROJECT_KEY);
      return;
    }

    setLoading(true);
    try {
      const [projectRes, driversRes, segmentTypesRes, segmentsRes, stakeholdersRes, permsRes] = await Promise.all([
        projectsAPI.get(projectId),
        driversAPI.list(projectId),
        segmentTypesAPI.list(projectId),
        segmentsAPI.list(projectId),
        stakeholdersAPI.listAll(projectId),
        api.get(`/projects/${projectId}/user-permissions`).catch(() => ({ data: { allowed_pages: null, allowed_segments: null, is_admin: true } })),
      ]);
      
      setCurrentProject(projectRes.data);
      setDrivers(driversRes.data);
      setSegmentTypes(segmentTypesRes.data);
      setSegments(segmentsRes.data);
      setUserPermissions(permsRes.data);
      
      // Check if current user is admin for this project
      // Handle both paginated and non-paginated response formats
      const stakeholdersList = stakeholdersRes.data.stakeholders || stakeholdersRes.data;
      const currentUserEmail = user?.email;
      const userStakeholder = Array.isArray(stakeholdersList) 
        ? stakeholdersList.find(s => s.email === currentUserEmail)
        : null;
      setIsProjectAdmin(userStakeholder?.is_admin || false);
      setIsAccountOwner(userStakeholder?.is_account_owner || false);
      
      // Save to localStorage for persistence
      localStorage.setItem(LAST_PROJECT_KEY, projectId);
    } catch (err) {
      console.error('Failed to load project data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refreshDrivers = useCallback(async () => {
    if (!currentProject) return;
    try {
      const response = await driversAPI.list(currentProject.id);
      setDrivers(response.data);
    } catch (err) {
      console.error('Failed to refresh drivers:', err);
    }
  }, [currentProject]);

  const refreshSegmentTypes = useCallback(async () => {
    if (!currentProject) return;
    try {
      const response = await segmentTypesAPI.list(currentProject.id);
      setSegmentTypes(response.data);
    } catch (err) {
      console.error('Failed to refresh segment types:', err);
    }
  }, [currentProject]);

  const refreshSegments = useCallback(async () => {
    if (!currentProject) return;
    try {
      const response = await segmentsAPI.list(currentProject.id);
      setSegments(response.data);
    } catch (err) {
      console.error('Failed to refresh segments:', err);
    }
  }, [currentProject]);

  const refreshAllSegmentData = useCallback(async () => {
    if (!currentProject) return;
    try {
      const [segmentTypesRes, segmentsRes] = await Promise.all([
        segmentTypesAPI.list(currentProject.id),
        segmentsAPI.list(currentProject.id),
      ]);
      setSegmentTypes(segmentTypesRes.data);
      setSegments(segmentsRes.data);
    } catch (err) {
      console.error('Failed to refresh segment data:', err);
    }
  }, [currentProject]);

  const refreshAdminStatus = useCallback(async () => {
    if (!currentProject || !user) return;
    try {
      const response = await stakeholdersAPI.listAll(currentProject.id);  // Use listAll for admin check
      // Handle both paginated and non-paginated response formats
      const stakeholdersList = response.data.stakeholders || response.data;
      const userStakeholder = Array.isArray(stakeholdersList) 
        ? stakeholdersList.find(s => s.email === user.email)
        : null;
      setIsProjectAdmin(userStakeholder?.is_admin || false);
      setIsAccountOwner(userStakeholder?.is_account_owner || false);
    } catch (err) {
      console.error('Failed to refresh admin status:', err);
    }
  }, [currentProject, user]);

  return (
    <ProjectContext.Provider value={{
      currentProject,
      projects,
      drivers,
      segmentTypes,
      segments,
      loading,
      isProjectAdmin,
      isAccountOwner,
      userPermissions,
      fetchProjects,
      selectProject,
      refreshDrivers,
      refreshSegmentTypes,
      refreshSegments,
      refreshAllSegmentData,
      refreshAdminStatus,
      setProjects,
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
